/**
 * Validation for an event's custom registration questions.
 *
 * Extracted from the guest form so the rules can be tested on their own and
 * reused wherever custom questions are answered. The form previously checked
 * only "was something entered"; the per-field `validation` setting was saved
 * and never read.
 */

export type CustomFieldType =
  | 'text' | 'email' | 'phone' | 'textarea'
  | 'select' | 'checkbox' | 'radio' | 'multiselect'
  | 'date' | 'time' | 'number';

export type CustomFieldValidation = 'none' | 'email' | 'phone' | 'url' | 'number';

export interface ValidatableField {
  fieldName: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  required?: boolean;
  validation?: CustomFieldValidation;
  options?: string[];
}

export type AnswerValue = string | boolean | string[] | undefined | null;
export type AnswerMap = { [fieldName: string]: AnswerValue };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+]?[0-9][0-9\s-]{7,15}$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+$/i;
const NUMBER = /^-?\d+(\.\d+)?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;      // value produced by <input type="date">
const TIME = /^\d{2}:\d{2}(:\d{2})?$/;   // value produced by <input type="time">

/**
 * True when a question has not been answered.
 *
 * Multi-select needs its own check: an empty array is truthy in JavaScript, so
 * a plain falsy test lets a required "select all that apply" through with
 * nothing chosen. A single tick box counts as answered only when ticked.
 */
export function isAnswerEmpty(field: ValidatableField, value: AnswerValue): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (field.fieldType === 'checkbox') return value !== true;
  return false;
}

/** The rule to apply, falling back to the field type when none was set. */
export function ruleFor(field: ValidatableField): CustomFieldValidation {
  if (field.validation && field.validation !== 'none') return field.validation;
  if (field.fieldType === 'email') return 'email';
  if (field.fieldType === 'phone') return 'phone';
  if (field.fieldType === 'number') return 'number';
  return 'none';
}

/** Message for a single answer, or null when it is acceptable. */
export function validateAnswer(field: ValidatableField, value: AnswerValue): string | null {
  if (isAnswerEmpty(field, value)) {
    return field.required ? `${field.fieldLabel} is required` : null;
  }

  // Only free text carries a format rule; choices are constrained by their options.
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;

  // Date and time inputs hand back a fixed shape; anything else means the
  // browser gave us something unexpected, or the value was typed by hand.
  if (field.fieldType === 'date' && !DATE.test(text)) {
    return `${field.fieldLabel} must be a valid date`;
  }
  if (field.fieldType === 'time' && !TIME.test(text)) {
    return `${field.fieldLabel} must be a valid time`;
  }

  switch (ruleFor(field)) {
    case 'email':
      return EMAIL.test(text) ? null : `${field.fieldLabel} must be a valid email address`;
    case 'phone':
      return PHONE.test(text) ? null : `${field.fieldLabel} must be a valid phone number`;
    case 'url':
      return URL_RE.test(text) ? null : `${field.fieldLabel} must be a valid link starting with http`;
    case 'number':
      return NUMBER.test(text) ? null : `${field.fieldLabel} must be a number`;
    default:
      return null;
  }
}

/**
 * Validates every question, returning errors keyed as the form expects
 * (`custom_<fieldName>`). An empty object means the answers are acceptable.
 */
export function validateCustomFields(
  fields: ValidatableField[],
  answers: AnswerMap
): { [key: string]: string } {
  const errors: { [key: string]: string } = {};
  for (const field of fields || []) {
    const message = validateAnswer(field, answers ? answers[field.fieldName] : undefined);
    if (message) errors[`custom_${field.fieldName}`] = message;
  }
  return errors;
}

/* ------------------------------------------------ the questions themselves */

/** Limits enforced by the Event model. Kept here so the form can check first. */
export const FIELD_LIMITS = {
  label: 300,
  fieldName: 60,
  option: 200,
  placeholder: 200,
  helpText: 300,
} as const;

/**
 * Checks the questions an admin has built, before the event is submitted.
 *
 * The server rejects an oversized question with a message like
 * "guestFormFields.22.fieldLabel: Field label cannot exceed 100 characters" —
 * counted from zero, after the whole form has been filled in. These messages
 * name the question the way it appears on screen, and are meant to be shown
 * while editing rather than on save.
 */
export function validateFieldDefinitions(
  fields: Array<{
    fieldLabel?: string;
    fieldName?: string;
    options?: string[];
    placeholder?: string;
    helpText?: string;
    fieldType?: string;
  }>
): string[] {
  const problems: string[] = [];
  const seenNames = new Map<string, number>();

  (fields || []).forEach((field, i) => {
    const n = i + 1;                                  // as numbered on screen
    const label = (field.fieldLabel || '').trim();
    const name = (field.fieldName || '').trim();

    if (!label) {
      problems.push(`Question ${n} has no wording.`);
    } else if (label.length > FIELD_LIMITS.label) {
      problems.push(
        `Question ${n} is ${label.length} characters; the limit is ${FIELD_LIMITS.label}. ` +
        `Shorten it, or move the detail into its help text.`
      );
    }

    if (!name) {
      problems.push(`Question ${n} has no field name.`);
    } else if (name.length > FIELD_LIMITS.fieldName) {
      problems.push(
        `Question ${n}: the field name is ${name.length} characters; the limit is ${FIELD_LIMITS.fieldName}.`
      );
    } else {
      const first = seenNames.get(name);
      if (first) {
        problems.push(
          `Question ${n} shares the field name "${name}" with question ${first}. ` +
          `Answers would overwrite each other — give one of them a different name.`
        );
      } else {
        seenNames.set(name, n);
      }
    }

    (field.options || []).forEach((option, oi) => {
      if ((option || '').length > FIELD_LIMITS.option) {
        problems.push(`Question ${n}, choice ${oi + 1} is longer than ${FIELD_LIMITS.option} characters.`);
      }
    });

    if ((field.helpText || '').length > FIELD_LIMITS.helpText) {
      problems.push(`Question ${n}: help text is longer than ${FIELD_LIMITS.helpText} characters.`);
    }

    const needsOptions = ['select', 'radio', 'multiselect'].includes(field.fieldType || '');
    if (needsOptions && (field.options || []).filter((o) => (o || '').trim()).length === 0) {
      problems.push(`Question ${n} is a choice question but has no choices yet.`);
    }
  });

  return problems;
}
